import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CategoryIcon from "@mui/icons-material/Category";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SearchIcon from "@mui/icons-material/Search";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { HideIfNoRole } from "../../../auth/authUi";
import AxionControls from "../../../components/controls/AxionControls";
import {
  activateSparePartType,
  deactivateSparePartType,
  getSparePartTypes,
} from "../../../fetchers/cmmsSparePartType";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { CMMS_MANAGER_ROLES } from "../../cmms/cmmsRoles";
import cmmsMessages from "../../cmms/cmmsMessages";
import { CmmsEmptyState, CmmsErrorState, formatCmmsError } from "../../cmms/components/CmmsStates";
import {
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getSwitchSx,
} from "../../cmms/assetHierarchyStyles";
import messages from "../messages";
import SparePartTypesDataGrid from "./SparePartTypesDataGrid";
import SparePartTypeFormDialog from "./SparePartTypeFormDialog";

/*
 * The spare part types tab of the CMMS settings page.
 *
 * StorageLocationsPanel with a different noun. No statistic tile for the same reason: a count of
 * parts per type is a second read of the whole register to answer a question nobody is blocked on
 * — retiring a type is never refused, and the parts carrying it keep naming it either way.
 *
 * The query key is shared with the spare part form's type dropdown on purpose. Adding a type here
 * invalidates it, so the register offers the new one without a reload.
 */
export default function SparePartTypesPanel() {
  const theme = useTheme();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const [companyKey] = useLocalStorage("companyKey", 1);

  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [formState, setFormState] = useState({ open: false, sparePartType: null });
  const [confirmation, setConfirmation] = useState(null);

  const typesQuery = useQuery(
    ["cmmsSparePartTypes", companyKey, includeInactive],
    () => getSparePartTypes(companyKey, includeInactive),
    { enabled: Boolean(companyKey), staleTime: 30000 },
  );

  const types = useMemo(() => typesQuery.data ?? [], [typesQuery.data]);

  // The same division the register draws: the endpoint owns which rows exist — that is what the
  // switch below asks it — and the browser owns which of them are on screen. The list arrives
  // whole, so the text is here to be matched.
  const visibleTypes = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return types;
    return types.filter((type) => (type.name ?? "").toLowerCase().includes(needle));
  }, [types, searchText]);

  const deactivateMutation = useMutation({
    mutationFn: (id) => deactivateSparePartType(companyKey, id),
  });

  const restoreMutation = useMutation({
    mutationFn: (id) => activateSparePartType(companyKey, id),
  });

  const hasActiveFilters = includeInactive || searchText.trim() !== "";

  const handleClearFilters = () => {
    setIncludeInactive(false);
    setSearchText("");
  };

  const handleAdd = () => setFormState({ open: true, sparePartType: null });
  const handleEdit = (row) => setFormState({ open: true, sparePartType: row });
  const closeForm = () => setFormState({ open: false, sparePartType: null });

  const handleDeactivate = (row) => {
    setConfirmation({
      title: intl.formatMessage(messages.sparePartTypeDeactivateTitle),
      message: intl.formatMessage(messages.sparePartTypeDeactivateMessage, { name: row.name }),
      confirmText: intl.formatMessage(messages.deactivateConfirm),
      onConfirm: async () => {
        try {
          await deactivateMutation.mutateAsync(row.id);
          setConfirmation(null);
          await queryClient.invalidateQueries({ queryKey: ["cmmsSparePartTypes"] });
          pushNotification(intl.formatMessage(messages.sparePartTypeToastDeactivated));
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.sparePartTypeDeactivateFailed)));
        }
      },
    });
  };

  const handleRestore = (row) => {
    setConfirmation({
      title: intl.formatMessage(messages.sparePartTypeRestoreTitle),
      message: intl.formatMessage(messages.sparePartTypeRestoreMessage, { name: row.name }),
      confirmText: intl.formatMessage(messages.restoreConfirm),
      onConfirm: async () => {
        try {
          await restoreMutation.mutateAsync(row.id);
          setConfirmation(null);
          await queryClient.invalidateQueries({ queryKey: ["cmmsSparePartTypes"] });
          pushNotification(intl.formatMessage(messages.sparePartTypeToastRestored));
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.sparePartTypeRestoreFailed)));
        }
      },
    });
  };

  const isMutating = deactivateMutation.isPending || deactivateMutation.isLoading
    || restoreMutation.isPending || restoreMutation.isLoading;

  return (
    <Box>
      {/*
        * No heading of its own — the tab above already says Spare part types, and repeating it two
        * lines down is a title nobody reads twice. The line that follows says something the label
        * cannot.
        */}
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        <FormattedMessage {...messages.sparePartTypesSubtitle} />
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ gap: 1, mb: 2 }}>
        {/*
          * Behind the route's own Admin gate as well. Belt and braces on purpose: the gate is one
          * line in AppRoutes.js, and a button that 403s is worse than one that was never offered.
          */}
        {/*
          * The module's own button styling, not MUI's default. palette.primary.main is
          * colors.primary[500] and so is the surface behind it — the same hex — so a bare
          * contained button is invisible until hovered.
          */}
        <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={getContainedButtonSx(cmmsColors)}
          >
            <FormattedMessage {...messages.addSparePartType} />
          </Button>
        </HideIfNoRole>

        <TextField
          size="small"
          sx={{ width: { xs: "100%", sm: 280 } }}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          label={intl.formatMessage(messages.filterSearch)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/*
          * Server-side, and the only way to a retired row: the endpoint leaves them out by
          * default, so without this switch there is no way to reach the restore button.
          */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
              sx={getSwitchSx(cmmsColors)}
            />
          }
          label={intl.formatMessage(messages.filterShowInactive)}
        />
      </Stack>

      {typesQuery.error &&
        <CmmsErrorState
          error={typesQuery.error}
          fallback={intl.formatMessage(messages.sparePartTypesLoadFailed)}
          onRetry={() => typesQuery.refetch()}
          isRetrying={typesQuery.isFetching}
        />
      }
      {!typesQuery.error &&
        <SparePartTypesDataGrid
          sparePartTypes={visibleTypes}
          // First load only, so a background refetch does not replace the list with a skeleton.
          isLoading={typesQuery.isLoading}
          showStatusColumn={includeInactive}
          onEdit={handleEdit}
          onDeactivate={handleDeactivate}
          onRestore={handleRestore}
          emptyState={
            hasActiveFilters
              ? <CmmsEmptyState
                  icon={<FilterAltOffIcon />}
                  title={intl.formatMessage(messages.sparePartTypesEmptyFilteredTitle)}
                  hint={intl.formatMessage(messages.emptyFilteredHint)}
                  action={
                    <Button size="small" variant="outlined" color="secondary" onClick={handleClearFilters}>
                      <FormattedMessage {...messages.filterClear} />
                    </Button>
                  }
                />
              : <CmmsEmptyState
                  icon={<CategoryIcon />}
                  title={intl.formatMessage(messages.sparePartTypesEmptyTitle)}
                  hint={intl.formatMessage(messages.sparePartTypesEmptyHint)}
                  action={
                    <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                        sx={getContainedButtonSx(cmmsColors)}
                      >
                        <FormattedMessage {...messages.addSparePartType} />
                      </Button>
                    </HideIfNoRole>
                  }
                />
          }
        />
      }

      <SparePartTypeFormDialog
        open={formState.open}
        sparePartType={formState.sparePartType}
        companyKey={companyKey}
        onClose={closeForm}
      />

      <AxionControls.AxionConfirmation
        open={Boolean(confirmation)}
        title={confirmation?.title}
        message={confirmation?.message}
        confirmText={confirmation?.confirmText ?? intl.formatMessage(cmmsMessages.confirm)}
        cancelText={intl.formatMessage(cmmsMessages.cancel)}
        onConfirm={confirmation?.onConfirm}
        onCancel={() => setConfirmation(null)}
      />
      <AxionControls.AxionBackdrop open={isMutating} />
    </Box>
  );
}
